import React, { useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { RootState, AppDispatch } from '../../src/redux/store';
import {
  PlannedMeal,
  selectWeekByStartDate,
  markMealAsCooked,
  unmarkMealAsCooked,
} from '../../src/redux/mealPlanSlice';
import { isEntryMeal, entryTitle } from '../../src/redux/mealEntry';
import { VerdictAvatars } from '../../src/components/household/VerdictAvatars';
import { useVerdictPrompt } from '../../src/components/household/VerdictPromptProvider';
import { Paprika, PaprikaTypography, PaprikaSpacing } from '../../src/styles/theme';
import { RemoteImage } from '../../src/components/shared/RemoteImage';
import { formatWeekday, formatWeekRange } from '../../src/utils/dateLocale';
import { RecipePlaceholder } from '../../src/components/shared/RecipePlaceholder';

function MealRecapRow({
  meal,
  onToggleCooked,
  onRate,
  resolveTitle,
}: {
  meal: PlannedMeal;
  onToggleCooked: () => void;
  onRate: () => void;
  resolveTitle: (recipeId: string) => string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const isCooked = meal.status === 'cooked';
  const imageSource = meal.recipe?.image;
  // Was an English array indexed by dayOfWeek, so the French recap read « MONDAY ».
  const dayName = meal.scheduledFor ? formatWeekday(meal.scheduledFor, i18n.language) : '';
  const title = isEntryMeal(meal)
    ? entryTitle(meal.entry!, resolveTitle, t)
    : meal.recipe?.title || meal.freeText || t('thisWeek.unknownRecipe');

  return (
    <View style={styles.mealRow}>
      {/* Image */}
      <View style={styles.mealImageContainer}>
        <RemoteImage
          uri={imageSource}
          variant="thumb"
          style={styles.mealImage}
          fallback={
            <RecipePlaceholder
              recipe={{
                title,
                ingredients: meal.recipe?.extendedIngredients?.map(i => i.name) ?? [],
                diets: meal.recipe?.diets,
              }}
              size={64}
              style={styles.mealImage}
              testID="week-recap-placeholder"
            />
          }
        />
      </View>

      {/* Content */}
      <View style={styles.mealContent}>
        <Text style={styles.mealDay} testID="recap-day">{dayName}</Text>
        <Text style={styles.mealTitle} numberOfLines={2}>{title}</Text>

        {/* Cooked toggle */}
        <TouchableOpacity
          style={[styles.cookedToggle, isCooked && styles.cookedToggleActive]}
          onPress={onToggleCooked}
        >
          <Feather
            name={isCooked ? 'check-circle' : 'circle'}
            size={16}
            color={isCooked ? Paprika.success : Paprika.textMuted}
          />
          <Text style={[styles.cookedText, isCooked && styles.cookedTextActive]}>
            {isCooked ? t('weekRecap.cooked') : t('weekRecap.notCooked')}
          </Text>
        </TouchableOpacity>

        {/* Who liked it. Tapping asks the table rather than writing a number
            behind their backs — same sheet every other cook gesture opens. */}
        {isCooked && (
          <TouchableOpacity onPress={onRate} style={styles.verdictRow}>
            <VerdictAvatars recipeId={String(meal.recipeId)} variant="paper" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

export default function WeekRecapScreen() {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { openVerdict } = useVerdictPrompt();
  const { week } = useLocalSearchParams<{ week: string }>();

  const weekStartDate = week || '';
  const meals = useSelector((state: RootState) => selectWeekByStartDate(state, weekStartDate));

  // Helper to resolve recipe titles for entry leftovers
  const resolveTitle = useMemo(() => {
    const titleMap: Record<string, string> = {};
    meals.forEach(m => {
      if (m.recipe?.id) {
        titleMap[String(m.recipe.id)] = m.recipe.title;
      }
    });
    return (recipeId: string) => titleMap[recipeId];
  }, [meals]);

  // Sort meals by day
  const sortedMeals = useMemo(() => {
    return [...meals].sort((a, b) => {
      const aDay = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
      const bDay = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
      return aDay - bDay;
    });
  }, [meals]);

  const cookedCount = sortedMeals.filter(m => m.status === 'cooked').length;
  const totalCount = sortedMeals.length;

  const handleToggleCooked = useCallback((meal: PlannedMeal) => {
    if (meal.status === 'cooked') {
      dispatch(unmarkMealAsCooked({ mealId: meal.id }));
    } else {
      dispatch(markMealAsCooked({ mealId: meal.id }));
    }
  }, [dispatch]);

  const handleRate = useCallback((meal: PlannedMeal) => {
    openVerdict({
      recipeId: String(meal.recipeId),
      recipeTitle: meal.recipe?.title ?? meal.freeText ?? '',
      surface: 'cook_log',
    });
  }, [openVerdict]);

  // Format week range for header
  const weekRange = useMemo(
    () => (weekStartDate ? formatWeekRange(weekStartDate, i18n.language, 'short') : ''),
    [weekStartDate, i18n.language],
  );

  if (!weekStartDate || totalCount === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <Feather name="arrow-left" size={24} color={Paprika.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('weekRecap.title', 'Week in Review')}</Text>
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>{t('weekRecap.noMeals', 'No meals found for this week.')}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
          <Feather name="arrow-left" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <View>
          <Text style={styles.headerTitle}>{t('weekRecap.title', 'Week in Review')}</Text>
          <Text style={styles.headerSubtitle} testID="recap-week-range">{weekRange}</Text>
        </View>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Summary */}
        <View style={styles.summaryCard}>
          <Text style={styles.summaryText}>
            {cookedCount === 0
              ? t('weekRecap.noneCooked', 'Tap each meal you cooked this week, then rate it.')
              : t('weekRecap.summary', 'You cooked {{cooked}} of {{total}} planned meals.', { cooked: cookedCount, total: totalCount })}
          </Text>
        </View>

        {/* Meal list */}
        {sortedMeals.map(meal => (
          <MealRecapRow
            key={meal.id}
            meal={meal}
            onToggleCooked={() => handleToggleCooked(meal)}
            onRate={() => handleRate(meal)}
            resolveTitle={resolveTitle}
          />
        ))}

        {/* Done button */}
        <TouchableOpacity
          style={styles.doneButton}
          onPress={() => router.back()}
        >
          <Text style={styles.doneButtonText}>{t('weekRecap.done', 'Done')}</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: Platform.OS === 'web' ? 24 : 56,
    paddingBottom: 16,
    backgroundColor: Paprika.background,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  headerTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: PaprikaSpacing.md,
    paddingBottom: 40,
  },
  summaryCard: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
  },
  summaryText: {
    fontSize: 15,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  mealRow: {
    flexDirection: 'row',
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  mealImageContainer: {
    marginRight: 14,
  },
  mealImage: {
    width: 64,
    height: 64,
    borderRadius: 10,
  },
  verdictRow: {
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  mealContent: {
    flex: 1,
    justifyContent: 'center',
  },
  mealDay: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: Paprika.textMuted,
    marginBottom: 2,
  },
  mealTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Paprika.textPrimary,
    marginBottom: 6,
  },
  cookedToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  cookedToggleActive: {},
  cookedText: {
    fontSize: 13,
    color: Paprika.textMuted,
  },
  cookedTextActive: {
    color: Paprika.success,
    fontWeight: '600',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: 16,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  doneButton: {
    marginTop: 24,
    backgroundColor: Paprika.accent,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
  },
  doneButtonText: {
    color: Paprika.onPrimary,
    fontSize: 16,
    fontWeight: '600',
  },
});
